import { createAction } from 'Utility/Redux';
import Point from '../../Utility/Point';
import { IPlatformState } from './IPlatformState';
import { LevelStatus } from '../Level/LevelStatus';
import { RewardType } from 'Utility/Rewards/RewardType';
import { ContextType, IContextRequest, ResponseStatus, ResponseType } from './ContextType';
import { IGameState } from './IGameState';
import { IEntryPointData } from './IEntryPointData';
import { CrossPromoStatus, IUserState } from './IUserState';
import { IPlayerState } from '../Player/IPlayerState';
import { MyPetMiniGameSceneParams } from 'Scene/MyPetMiniGameScene';
import { PreBoosterType } from '../Card/BoosterType';
import { ISceneParams } from 'Scene/Scene';
import { IPerSessionData } from 'State/Game/IPerSessionData';
import { IEventsState } from 'State/Events/IEventsState';
import { AdDebugMode } from 'State/Game/AdDebugMode';
import { IPurchaseConsumptionData } from 'State/Player/IAP/IAPStateHelper';
import { ILevelDataFile } from 'System/Level/Data/ILevelDataFile';
import { IAPDebugMode } from './IAPDebugMode';

export enum Types {
    SET_STATE = 'setState',
    SET_PER_SESSION_DATA = 'setPerSessionData',
    ENABLE_IAP = 'enableIAP',
    SET_SCENE_LOADED = 'setSceneLoaded',
    SET_SCENE_LOADING = 'setSceneLoading',
    GAME_LOADED = 'gameLoaded',
    LOAD_WORLD = 'loadWorld',
    LOAD_LEVEL = 'loadLevel',
    LOAD_LUCKY_TURNIPS = 'loadLuckyTurnips',
    LOAD_MY_PET_MINI_GAME = 'loadMyPetMiniGame',
    LOAD_FARM_SCENE = 'loadFarmScene',
    LOAD_PLINKO_SCENE = 'loadPlinkoScene',
    RESTART_LEVEL = 'restartLevel',
    SET_CONTEXT = 'setContext',
    CLEAR_CONTEXT = 'clearContext',
    RESTORE_DEFAULT_CONTEXT = 'restoreDefaultContext',
    SET_CANVAS_SIZE = 'setCanvasSize',
    END_LEVEL = 'endLevel',
    SET_LOADED_DATA = 'setLoadedData',
    FARM_UNLOCK_NEW_ITEMS = 'farmUnlockNewItems',
    SET_CROSS_PROMO = 'setCrossPromo',
    SET_AD_DEBUG_MODE = 'setAdDebugMode',
    SET_IAP_DEBUG_MODE = 'setIAPDebugMode',
    CONNECTED_TO_GBC = 'connectedToGBC',
    DISCONNECTED_FROM_GBC = 'disconnectedFromGBC',
    SET_GAME_PAUSED = 'setGamePaused',
    LOAD_LEVEL_DEBUG = 'loadLevelDebug',
}

export enum GiftType {
    coins = 'coins',
    lives = 'lives',
    joker = 'joker',
    TNT = 'TNT',
    Shuffle = 'Shuffle',
}

export interface IBotMessageGift {
    giftData: IGiftData[];
    id: string;
}

export interface IGiftData {
    amount: number;
    itemId: RewardType;
}

export interface IGameLoadData {
    userState: Partial<IUserState>;
    platform: Partial<IPlatformState>;
    playerState: IPlayerState;
    entryPoint: IEntryPointData;
    context: string;
    eventsState: IEventsState;
}

export enum LevelFailReason {
    TNT_EXPLODED = 'tntExploded',
    NO_MORE_MATCHES = 'noMoreMatches',
    QUIT_LEVEL = 'quitLevel',
}

export interface IEndLevelData {
    status: LevelStatus.RESTARTING | LevelStatus.FAILED | LevelStatus.COMPLETE | LevelStatus.EXITING;
    firstComplete: boolean;
    failReason?: LevelFailReason;
    preBoosters?: PreBoosterType[];
}

export interface ILoadWorldMetaData {
    worldNumber?: number;
    levelComplete?: boolean;

    /**
     * Temporary fix for UI that needs to activate after the world loads.
     */
    clearUI?: boolean;
    firstComplete?: boolean;
    firstLoad?: boolean;
    starsGained?: number;
    petSpawnLevel?: number;
}

export const GameActions = {
    setState: createAction(Types.SET_STATE, resolve => (state: IGameState) => resolve<IGameState>(state)),
    setPerSessionData: createAction(Types.SET_PER_SESSION_DATA, resolve => (state: Partial<IPerSessionData>) =>
        resolve(state)
    ),
    gameLoaded: createAction(Types.GAME_LOADED),
    setLoadedData: createAction(Types.SET_LOADED_DATA, resolve => (data: IGameLoadData) =>
        resolve<IGameLoadData>(data)
    ),
    loadWorld: createAction(Types.LOAD_WORLD, resolve => (params?: ILoadWorldMetaData) =>
        resolve<ILoadWorldMetaData | undefined>(params)
    ),

    loadLevel: createAction(
        Types.LOAD_LEVEL,
        resolve => (payload: {
            levelNumber: number;
            preBoosters: PreBoosterType[];
            restart?: boolean;
            isLevelTest?: boolean;
        }) => resolve(payload)
    ),

    loadLuckyTurnips: createAction(Types.LOAD_LUCKY_TURNIPS, resolve => () => resolve()),

    loadMyPetMiniGame: createAction(Types.LOAD_MY_PET_MINI_GAME, resolve => (params: MyPetMiniGameSceneParams) =>
        resolve(params)
    ),

    loadFarmScene: createAction(Types.LOAD_FARM_SCENE, resolve => (params: ISceneParams) => resolve(params)),

    loadPlinkoScene: createAction(Types.LOAD_PLINKO_SCENE, resolve => (params: ISceneParams) => resolve(params)),

    endLevel: createAction(Types.END_LEVEL, resolve => (data: IEndLevelData) => resolve(data)),

    restartLevel: createAction(Types.RESTART_LEVEL, resolve => (levelNumber: number) => resolve(levelNumber)),

    setContext: createAction(Types.SET_CONTEXT, resolve => (context: ContextType, data?: {}) =>
        resolve<IContextRequest>({ context: context, data: data })
    ),

    clearContextData: createAction(Types.CLEAR_CONTEXT, resolve => () => resolve()),

    restoreContext: createAction(
        Types.RESTORE_DEFAULT_CONTEXT,
        resolve => (status: ResponseStatus, type: ResponseType, data?: any) =>
            resolve<{
                status: ResponseStatus;
                type: ResponseType;
                data: any;
            }>({
                status: status,
                type: type,
                data: data,
            })
    ),

    setCanvasSize: createAction(Types.SET_CANVAS_SIZE, resolve => (size: Point) => resolve<Point>(size)),
    setSceneLoading: createAction(Types.SET_SCENE_LOADING),
    setSceneLoaded: createAction(Types.SET_SCENE_LOADED),

    enableIAP: createAction(Types.ENABLE_IAP, resolve => (unconsumedPurchases: IPurchaseConsumptionData[]) =>
        resolve<IPurchaseConsumptionData[]>(unconsumedPurchases)
    ),

    setCrossPromo: createAction(
        Types.SET_CROSS_PROMO,
        resolve => (crossPromoResult: { playerState: IPlayerState; crossPromoStatus: CrossPromoStatus }) =>
            resolve(crossPromoResult)
    ),

    setAdDebugMode: createAction(Types.SET_AD_DEBUG_MODE, resolve => (mode: AdDebugMode) => resolve(mode)),
    setIAPDebugMode: createAction(Types.SET_IAP_DEBUG_MODE, resolve => (mode: IAPDebugMode) => resolve(mode)),

    connectedToGBC: createAction(Types.CONNECTED_TO_GBC),
    disconnectedFromGBC: createAction(Types.DISCONNECTED_FROM_GBC),
    setGamePaused: createAction(Types.SET_GAME_PAUSED, resolve => (paused: boolean) => resolve(paused)),
    
    loadLevelDebug: createAction(
        Types.LOAD_LEVEL_DEBUG,
        resolve => (payload: ILevelDataFile) => resolve(payload)
    ),
};
